import {
  useEnvironment,
  useExperiments,
  useTranslation,
} from '@wix/yoshi-flow-editor';
import React, { useEffect } from 'react';
import { ModalFacadeProps } from '../..';
import { Modal } from '../../../../components/MyBookings/Components/Modal';
import { Reschedule } from '../../../../components/MyBookings/Widget/ActionsButtons/Reschedule';
import { CancellationModalDataHooks } from '../../../../components/MyBookings/Widget/datahooks';
import {
  ActionButtonUiTypes,
  BookingsGroup,
  CloseModalTriggers,
  RescheduleButtonOrigin,
} from '../../../../types';
import { ExperimentsConsts } from '../../../../consts/experiments';
import {
  hasSubscriptionInfo,
  isCancellationFeePolicy,
  isCourse,
  isRescheduleAble,
} from '../../../../utils/bookInfo';
import { useApplicationContext } from '../../../hooks/useApplicationContext';
import { useGroupHeaderGetters } from '../../../hooks/useGroupHeaderGetter';
import { CancellationFeeModalContent } from './CancellationFeeModalsContent';
import { classes } from './CancellationModal.st.css';

export interface CancellationModalProps {
  group: BookingsGroup;
}

export const CancellationModal: React.FC<
  CancellationModalProps & ModalFacadeProps
> = ({ group }) => {
  const { actions, cancellationFeeInfo } = useApplicationContext();
  const { t } = useTranslation();
  const { getTitle } = useGroupHeaderGetters();
  const { isMobile } = useEnvironment();
  const { experiments } = useExperiments();
  const groupFeeId = group.groupId || group.bookings[0]?.bookingId;
  const feeInfo = cancellationFeeInfo?.[groupFeeId];
  const withCancellationFeePolicy = isCancellationFeePolicy(group);
  const shouldGetCancellationFeeInfo = withCancellationFeePolicy && !feeInfo;

  useEffect(() => {
    if (shouldGetCancellationFeeInfo) {
      actions.getCancellationFeeInfo({
        group,
      });
    }
  }, []);

  const handleOnClose = () => {
    actions.deleteCancellationFeeInfo({ group });
    actions.closeModal({ triggeredBy: CloseModalTriggers.CloseModal });
  };

  const handlePrimaryButtonClick = () => {
    actions.clickOnCancel({
      group,
    });
    actions.closeModal({});
  };

  const handleSecondaryButtonClick = () => {
    actions.deleteCancellationFeeInfo({ group });
    actions.closeModal({ triggeredBy: CloseModalTriggers.CloseModal });
  };

  const renderCancellationModalWithRescheduleContent = () => (
    <Modal
      onClose={handleOnClose}
      dataHook={CancellationModalDataHooks.Reschedule}
      modalTitle={t(
        'app.my-bookings-widget.cancellation-modal-with-reschedule.title',
      )}
      modalSubtitle={t(
        'app.my-bookings-widget.cancellation-modal-with-reschedule.content',
      )}
      primaryButton={{
        context: {
          label: t(
            'app.my-bookings-widget.cancellation-modal-with-reschedule.primary-button.label',
          ),
          onClick: handlePrimaryButtonClick,
        },
      }}
      secondaryButton={{
        component: (
          <Reschedule
            origin={RescheduleButtonOrigin.CancellationModal}
            group={group}
            fullWidthButton={isMobile}
            type={ActionButtonUiTypes.BUTTON}
            className={classes.rescheduleButton}
          />
        ),
      }}
    />
  );

  const renderCancellationModalContent = () => (
    <Modal
      onClose={handleOnClose}
      dataHook={CancellationModalDataHooks.Cancellation}
      modalTitle={t('app.my-bookings-widget.cancellation-modal.title')}
      modalSubtitle={t('app.my-bookings-widget.cancellation-modal.content', {
        serviceName: getTitle(group),
      })}
      primaryButton={{
        context: {
          label: t(
            'app.my-bookings-widget.cancellation-modal.primary-button.label',
          ),
          onClick: handlePrimaryButtonClick,
        },
      }}
      secondaryButton={{
        context: {
          label: t(
            'app.my-bookings-widget.cancellation-modal.secondary-button.label',
          ),
          onClick: handleSecondaryButtonClick,
        },
      }}
    />
  );

  const renderGroupCancellationModalContent = () => (
    <Modal
      onClose={handleOnClose}
      dataHook={CancellationModalDataHooks.GroupCancellation}
      modalTitle={t('app.my-bookings-widget.group-cancellation-modal.title')}
      modalSubtitle={t(
        'app.my-bookings-widget.group-cancellation-modal.content',
      )}
      primaryButton={{
        context: {
          label: t(
            'app.my-bookings-widget.group-cancellation-modal.primary-button.label',
          ),
          onClick: handlePrimaryButtonClick,
        },
      }}
      secondaryButton={{
        context: {
          label: t(
            'app.my-bookings-widget.group-cancellation-modal.secondary-button.label',
          ),
          onClick: handleSecondaryButtonClick,
        },
      }}
    />
  );

  const renderCourseCancellationModalContent = () => (
    <Modal
      onClose={handleOnClose}
      dataHook={CancellationModalDataHooks.CourseCancellation}
      modalTitle={t('app.my-bookings-widget.course-cancellation-modal.title')}
      modalSubtitle={t(
        'app.my-bookings-widget.course-cancellation-modal.content',
        { courseName: getTitle(group) },
      )}
      primaryButton={{
        context: {
          label: t(
            'app.my-bookings-widget.course-cancellation-modal.primary-button.label',
          ),
          onClick: handlePrimaryButtonClick,
        },
      }}
      secondaryButton={{
        context: {
          label: t(
            'app.my-bookings-widget.course-cancellation-modal.secondary-button.label',
          ),
          onClick: handleSecondaryButtonClick,
        },
      }}
    />
  );

  const renderSubscriptionCourseCancellationModalContent = () => (
    <Modal
      onClose={handleOnClose}
      dataHook={CancellationModalDataHooks.SubscriptionCourseCancellation}
      modalTitle={t('app.my-bookings-widget.course-cancellation-modal.title')}
      modalSubtitle={t(
        'app.my-bookings-widget.course-cancellation-modal.subscription-content',
      )}
      primaryButton={{
        context: {
          label: t(
            'app.my-bookings-widget.course-cancellation-modal.primary-button.label',
          ),
          onClick: handlePrimaryButtonClick,
        },
      }}
      secondaryButton={{
        context: {
          label: t(
            'app.my-bookings-widget.course-cancellation-modal.secondary-button.label',
          ),
          onClick: handleSecondaryButtonClick,
        },
      }}
    />
  );

  const renderLoaderModalContent = () => (
    <Modal
      isLoading={true}
      dataHook={CancellationModalDataHooks.CancellationLoaderModal}
      modalTitle={t('app.my-bookings-widget.course-cancellation-modal.title')}
    />
  );

  const renderModalContent = () => {
    if (shouldGetCancellationFeeInfo) {
      return renderLoaderModalContent();
    } else if (feeInfo?.totalFee?.value) {
      return <CancellationFeeModalContent group={group} feeInfo={feeInfo} />;
    } else if (isRescheduleAble(group, experiments)) {
      return renderCancellationModalWithRescheduleContent();
    } else if (group.bookings.length > 1) {
      return renderGroupCancellationModalContent();
    } else if (
      isCourse(group) &&
      hasSubscriptionInfo(group) &&
      experiments.enabled(ExperimentsConsts.SubscriptionPricingUoU)
    ) {
      return renderSubscriptionCourseCancellationModalContent();
    } else if (isCourse(group)) {
      return renderCourseCancellationModalContent();
    } else {
      return renderCancellationModalContent();
    }
  };

  return (
    <div data-hook={CancellationModalDataHooks.Main}>
      {renderModalContent()}
    </div>
  );
};
